/**
 * 처방 등록 시 PDF를 페이지별 JPEG로 바꾼다 — 앱과 같은 규칙.
 * 앱(rudalink prescription_upload_bottom_sheet.dart `_handlePdfFileUpload`)은
 * 페이지를 2배 해상도 · 흰 배경 JPEG로 렌더링해 `{PDF이름}_page_{n}.jpg`로 올리고 원본 PDF는 올리지 않는다.
 * 흰 배경은 OCR 호환성을 위한 것이다.
 */
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/** 앱과 같은 렌더링 배율 */
const RENDER_SCALE = 2;
const JPEG_QUALITY = 0.92;
/** 앱과 같이 페이지를 3장씩 동시에 렌더링한다 */
const CONCURRENCY = 3;

export const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

const ensureWorker = () => {
  if (!GlobalWorkerOptions.workerSrc) {
    GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
  }
};

const canvasToJpeg = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('JPEG 변환에 실패했어요')),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });

/**
 * PDF 한 개를 페이지 순서대로 JPEG File 배열로 바꾼다.
 * @param onProgress 완료한 페이지 수 / 전체 페이지 수
 */
export const convertPdfToJpegPages = async (
  pdfFile: File,
  onProgress?: (done: number, total: number) => void,
): Promise<File[]> => {
  ensureWorker();
  const loadingTask = getDocument({
    data: new Uint8Array(await pdfFile.arrayBuffer()),
  });
  const pdf = await loadingTask.promise;
  // 앱과 같은 파일명 규칙: 확장자를 뺀 이름 + _page_{0부터}
  const baseName = pdfFile.name.replace(/\.pdf$/i, '');
  const total = pdf.numPages;
  const pages: File[] = new Array(total);
  let done = 0;

  const renderPage = async (index: number) => {
    const page = await pdf.getPage(index + 1);
    const viewport = page.getViewport({ scale: RENDER_SCALE });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas를 만들 수 없어요');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    try {
      // intent 'print'은 requestAnimationFrame을 쓰지 않는다.
      // 기본(display)은 화면 갱신 타이밍에 맞춰 그려서, 변환 중 다른 탭으로 가면 멈춘다
      await page.render({
        canvas,
        canvasContext: ctx,
        viewport,
        intent: 'print',
      }).promise;
      const blob = await canvasToJpeg(canvas);
      pages[index] = new File([blob], `${baseName}_page_${index}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    } finally {
      page.cleanup();
      canvas.width = 0;
      canvas.height = 0;
    }
    done += 1;
    onProgress?.(done, total);
  };

  try {
    let next = 0;
    const worker = async () => {
      while (next < total) {
        const index = next;
        next += 1;
        await renderPage(index);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, total) }, worker),
    );
  } finally {
    await pdf.cleanup();
    await loadingTask.destroy();
  }
  return pages;
};
